import { useTranslation } from "next-i18next";
import {
  LCPhuketDistrict,
  LCSamuiDistrict,
  LayoutObjectTypeCatalog,
} from "../types/complexPage";
import { useCatalogFilter } from "./useCatalogFilter";
import { ICatalogObject } from "../types/catalogPage";
import { getFilteredProperties } from "./useFilteredProperties";

export const useTranslateCatalog = ({
  propertyType,
  island,
  samuiDistrict,
  phuketDistrict,
  dealType,
  properties,
}: {
  propertyType:
    | LayoutObjectTypeCatalog
    | LayoutObjectTypeCatalog[]
    | "realty"
    | "flat"
    | null;
  island: "phuket" | "samui" | null;
  samuiDistrict: LCSamuiDistrict | LCSamuiDistrict[] | null;
  phuketDistrict: LCPhuketDistrict | LCPhuketDistrict[] | null;
  dealType: "sale" | "rent";
  properties: ICatalogObject[];
}) => {
  const { query, isOriginalCatalog, isRootCatalog } = useCatalogFilter();
  const { t } = useTranslation("index");

  const translateDealType = () => ({
    dealTypeString: t(`catalog.${dealType}`),
    dealTypeh1String: t(`catalog.h1${dealType}`),
  });

  const translatePropertyType = (isH1?: boolean) => {
    const getTranslationKey = (type: string) => `declinable.${isH1 ? "of" : ""}${type}`;
    if (query.propertyType && !isRootCatalog) {
      if (Array.isArray(query.propertyType)) {
        return query.propertyType.length === 1
          ? t(getTranslationKey(query.propertyType[0]))
          : t(getTranslationKey("realty"));
      }
      return t(getTranslationKey(query.propertyType));
    }
    return t(getTranslationKey((propertyType as string) || "realty"));
  };

  const translateDistrict = () => {
    const mergeDistricts = <T>(
      queryDistrict?: T | T[],
      additionalDistrict?: T | T[] | null,
    ) =>
      Array.from(
        new Set<T>(
          ([] as T[])
            .concat(queryDistrict || [], additionalDistrict || [])
            .filter(Boolean),
        ),
      ) as T[];

    const sDistrict = mergeDistricts<LCSamuiDistrict>(query.samuiDistrict, samuiDistrict);
    const pDistrict = mergeDistricts<LCPhuketDistrict>(
      query.phuketDistrict,
      phuketDistrict,
    );

    const isSubset = (
      district: (LCPhuketDistrict | LCSamuiDistrict)[],
      subset: (LCPhuketDistrict | LCSamuiDistrict)[],
    ) => subset.every((d) => district.includes(d));

    const districtCases = [
      { name: "Kata", districts: ["karon", "kata", "kata-noi"] },
      { name: "Bang-Tao", districts: ["bang-tao", "lagoon", "layan"] },
      { name: "Rawai", districts: ["rawai", "nai-harn"] },
    ];

    const matchingDistrict = districtCases.find(
      ({ districts }: { districts: any[] }) =>
        isSubset(pDistrict, districts) &&
        districts.every(
          (d: LCPhuketDistrict) =>
            pDistrict.includes(d) ||
            !getFilteredProperties(properties, { phuketDistrict: d }, "usd").length,
        ),
    );

    const isSingleSamuiDistrict =
      pDistrict.length === 0 && sDistrict.length === 1 && !sDistrict.includes("all");
    const isSinglePhuketDistrict =
      sDistrict.length === 0 &&
      !samuiDistrict &&
      (matchingDistrict || (pDistrict.length === 1 && !pDistrict.includes("all")));

    const isMultipleSamuiDistrict =
      pDistrict.length === 0 && (sDistrict.length > 1 || sDistrict.includes("all"));
    const isMultiplePhuketDistrict =
      sDistrict.length === 0 && (pDistrict.length > 1 || pDistrict.includes("all"));
    const formatDistrictString = (
      prefix: string,
      district: string,
      matchingDistrictName?: string,
    ) => {
      if (matchingDistrictName) {
        return ` ${prefix} ${t("locations." + matchingDistrictName.toLowerCase())}`;
      }
      return district ? ` ${prefix} ${t("locations." + district)}` : "";
    };

    const getDistrictString = () => {
      if (isRootCatalog) {
        if (phuketDistrict && phuketDistrict !== "all") {
          return formatDistrictString(
            t("locations.in"),
            phuketDistrict as LCPhuketDistrict,
          );
        }
        if (samuiDistrict && samuiDistrict !== "all") {
          return formatDistrictString(
            t("locations.in"),
            samuiDistrict as LCSamuiDistrict,
          );
        }
      }
      if (isSingleSamuiDistrict) {
        return formatDistrictString(
          t("locations.in"),
          (samuiDistrict as LCSamuiDistrict) || sDistrict[0],
        );
      }
      if (isSinglePhuketDistrict) {
        return formatDistrictString(
          t("locations.in"),
          (phuketDistrict as LCPhuketDistrict) || pDistrict[0],
          matchingDistrict?.name,
        );
      }
      return "";
    };

    const getDistrictH1String = () => {
      if (isRootCatalog) {
        if (phuketDistrict && phuketDistrict !== "all") {
          return `, ${t("locations." + phuketDistrict)}`;
        }
        if (samuiDistrict && samuiDistrict !== "all") {
          return `, ${t("locations." + samuiDistrict)}`;
        }
      }
      if (isSingleSamuiDistrict) {
        return `, ${t("locations." + (samuiDistrict || sDistrict[0]))}`;
      }
      if (isSinglePhuketDistrict) {
        return matchingDistrict
          ? `, ${t("locations." + matchingDistrict.name.toLowerCase())}`
          : `, ${t("locations." + (phuketDistrict || pDistrict[0]))}`;
      }
      return "";
    };

    return {
      sDistrict,
      pDistrict,
      isSingleSamuiDistrict,
      isSinglePhuketDistrict,
      isMultipleSamuiDistrict,
      isMultiplePhuketDistrict,
      matchingDistrict,
      districtString: getDistrictString(),
      districth1String: getDistrictH1String(),
    };
  };

  const getIslandString = (districtData: ReturnType<typeof translateDistrict>) => {
    const {
      sDistrict,
      pDistrict,
      isSingleSamuiDistrict,
      isSinglePhuketDistrict,
      isMultipleSamuiDistrict,
      isMultiplePhuketDistrict,
    } = districtData;

    if (isRootCatalog && island && !phuketDistrict && !samuiDistrict) {
      return `, ${t(`locations.${island}`)}`;
    }
    if (isSingleSamuiDistrict || isSinglePhuketDistrict) return "";
    if (isMultipleSamuiDistrict) return `, ${t("locations.samui")}`;
    if (isMultiplePhuketDistrict) return `, ${t("locations.phuket")}`;
    if (pDistrict.length && sDistrict.length) return `, ${t("locations.thailand")}`;
    return ` ${t("locations.inthailand")}`;
  };

  const { dealTypeString, dealTypeh1String } = translateDealType();
  const propertyTypeString = translatePropertyType();
  const propertyTypeh1String = translatePropertyType(true);
  const districtData = translateDistrict();
  const islandString = getIslandString(districtData);

  if (isOriginalCatalog) {
    return {
      title: `${t("meta.catalog.title")}${query.page ? ` - ${t("catalog.page")} ${query.page}` : ''}`,
      description: `${t("meta.catalog.description")}${query.page ? ` - ${t("catalog.page")} ${query.page}` : ''}`,
      h1: t("salesCatalog"),
    };
  }

  return {
    title: `${dealTypeString} ${propertyTypeString}${districtData.districtString}${islandString}${t("catalog.prices")}${query.page ? ` - ${t("catalog.page")} ${query.page}` : ""}`,
    description: `${dealTypeh1String} ${propertyTypeh1String}${districtData.districtString}${islandString}${t("catalog.inCompany")}${query.page ? ` - ${t("catalog.page")} ${query.page}` : ""}`,
    h1: `${dealTypeh1String} ${propertyTypeh1String}${districtData.districth1String}${islandString}`,
  };
};
